// @intent: Pixi overlay that draws pulsing outlines for render-model highlighted bounds
// @flow: PixiRuntimeCanvas passes RenderNode root and ids -> resolveRenderNodeBounds -> useTick redraws outline graphics
// @stale: update if highlight visual token or RenderNode bounds semantics change

import '@pixi/react';
import { useTick } from '@pixi/react';
import { useRef } from 'react';
import { type Graphics as PixiGraphics } from 'pixi.js';
import {
  resolveRenderNodeBounds,
  type RenderContainerNode,
  type RenderNodeBounds,
} from '@mintyads/playable-api';

const MINT_COLOR = 0x86e8c4;
const STROKE_WIDTH = 2;
const PADDING = 4;

function PixiHighlightOverlayInner({
  layers,
}: {
  layers: ReadonlyArray<RenderNodeBounds>;
}) {
  const gRef = useRef<PixiGraphics | null>(null);

  useTick(() => {
    const g = gRef.current;
    if (!g) return;
    g.clear();
    if (layers.length === 0) return;

    const alpha = 0.4 + 0.4 * Math.sin((Date.now() / 1000) * Math.PI * 2);
    g.alpha = alpha;

    for (const layer of layers) {
      const x = layer.x - PADDING;
      const y = layer.y - PADDING;
      const w = layer.width + PADDING * 2;
      const h = layer.height + PADDING * 2;
      g.roundRect(x, y, w, h, 6);
      g.stroke({ color: MINT_COLOR, width: STROKE_WIDTH });
    }
  });

  return (
    <pixiGraphics
      ref={(node: PixiGraphics | null) => {
        gRef.current = node;
      }}
      draw={() => {
        /* useTick drives all drawing; draw prop is required by @pixi/react v8 pixiGraphics intrinsic */
      }}
    />
  );
}

interface PixiHighlightOverlayProps {
  root: RenderContainerNode;
  highlightedLayerIds: readonly string[];
}

export function PixiHighlightOverlay({ root, highlightedLayerIds }: PixiHighlightOverlayProps) {
  const layers = highlightedLayerIds
    .map((id) => resolveRenderNodeBounds(root, id))
    .filter((layer): layer is RenderNodeBounds => layer !== null);

  if (layers.length === 0) return null;

  return <PixiHighlightOverlayInner layers={layers} />;
}
